import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertCircle, Clock, CheckCircle2, XCircle, Filter } from 'lucide-react';

const FollowUpManagement = () => {
  const { token } = useContext(AuthContext);
  const [followups, setFollowups] = useState([]);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    overdue_only: false
  });

  useEffect(() => {
    fetchFollowups();
  }, [filters]);

  const fetchFollowups = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.overdue_only) params.append('overdue_only', 'true');

      const response = await axios.get(`${API}/followups?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowups(response.data);
    } catch (error) {
      toast.error('Failed to fetch follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (followupId, newStatus) => {
    try {
      await axios.put(
        `${API}/followups/${followupId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      fetchFollowups();
      if (selectedFollowup?.id === followupId) {
        setSelectedFollowup({ ...selectedFollowup, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Critical: 'bg-red-100 text-red-800 border-red-300',
      High: 'bg-orange-100 text-orange-800 border-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colors[priority] || colors.Medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      Open: 'bg-blue-100 text-blue-800 border-blue-300',
      'In Progress': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Completed: 'bg-green-100 text-green-800 border-green-300',
      Cancelled: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[status] || colors.Open;
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'Completed' || status === 'Cancelled') return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Follow-Up Management</h2>
        <p className="text-gray-600 mt-1">Track and manage follow-up tasks from inspections</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Filters:</span>
            </div>

            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>

            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            >
              <option value="">All Priorities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
            </select>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.overdue_only}
                onChange={(e) => setFilters({ ...filters, overdue_only: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Overdue Only</span>
            </label>

            {(filters.status || filters.priority || filters.overdue_only) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ status: '', priority: '', overdue_only: false })}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Follow-up List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Follow-Up Tasks ({followups.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {followups.map((followup) => (
                  <div
                    key={followup.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedFollowup?.id === followup.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedFollowup(followup)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{followup.property_name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{followup.deficiency_summary}</p>
                      </div>
                      {isOverdue(followup.due_date, followup.status) && (
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 ml-2" />
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(followup.priority)}`}>
                        {followup.priority}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(followup.status)}`}>
                        {followup.status}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                        Due: {_fmtDate(followup.due_date)}
                      </span>
                    </div>
                  </div>
                ))}

                {followups.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No follow-up tasks found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Follow-up Details */}
        <div className="lg:col-span-1">
          {selectedFollowup ? (
            <Card>
              <CardHeader>
                <CardTitle>Task Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Property</label>
                  <p className="text-gray-900 font-semibold">{selectedFollowup.property_name}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Section</label>
                  <p className="text-gray-900">{selectedFollowup.section || 'N/A'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Item</label>
                  <p className="text-gray-900">{selectedFollowup.item_description || 'N/A'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Issue</label>
                  <p className="text-gray-900">{selectedFollowup.deficiency_summary}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Required Action</label>
                  <p className="text-gray-900">{selectedFollowup.required_action}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Safety Risk</label>
                  <p className="text-gray-900">{selectedFollowup.safety_risk_level}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Due Date</label>
                  <p className="text-gray-900">
                    {_fmtDate(selectedFollowup.due_date)}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Assigned To</label>
                  <p className="text-gray-900">{selectedFollowup.assigned_to || 'Unassigned'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Update Status</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Open', 'In Progress', 'Completed', 'Cancelled'].map((status) => (
                      <Button
                        key={status}
                        size="sm"
                        variant={selectedFollowup.status === status ? 'default' : 'outline'}
                        onClick={() => handleStatusUpdate(selectedFollowup.id, status)}
                        disabled={selectedFollowup.status === status}
                      >
                        {status}
                      </Button>
                    ))}
                  </div>
                </div>

                {selectedFollowup.photos && selectedFollowup.photos.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-2 block">Photos</label>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedFollowup.photos.map((photo, idx) => (
                        <div key={idx} className="aspect-square bg-gray-100 rounded border">
                          <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover rounded" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                Select a follow-up task to view details
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowUpManagement;
